import { CheckBox } from 'lego-on-react';
import React from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/AliceSkillForm';
import block from 'propmods';

import { filterInterfaces, getSupportedSurfaces, ImplicitSurface, Interface, Surface } from '../../lib/surfaces';
import { Field, Header } from '../Form/Form';

import AutoIcon from './icons/auto.svg';
import DesktopIcon from './icons/desktop.svg';
import MobileIcon from './icons/mobile.svg';
import NavigatorIcon from './icons/navigator.svg';
import StationIcon from './icons/station.svg';

import './InterfaceSelector.scss';

const b = block('InterfaceSelector');
const t = i18n(keyset);

const InterfaceLabel = {
    [Interface.Screen]: t('surface-option-need-screen'),
};

const surfaceIcons = [
    {
        title: t('surface-windows'),
        surface: ImplicitSurface.Desktop,
        Icon: DesktopIcon,
    },
    {
        title: t('surface-ya-app'),
        surface: ImplicitSurface.Mobile,
        Icon: MobileIcon,
    },
    {
        title: t('surface-navi'),
        surface: Surface.Navigator,
        Icon: NavigatorIcon,
    },
    {
        title: t('surface-station'),
        surface: Surface.Station,
        Icon: StationIcon,
    },
    {
        title: t('surface-auto'),
        surface: Surface.Auto,
        Icon: AutoIcon,
    },
];

interface InterfaceCheckboxProps extends InterfaceSelectorProps {
    type: Interface;
}

const InterfaceCheckbox: React.SFC<InterfaceCheckboxProps> = (props) => (
    <div {...b('interface')}>
        <CheckBox
            theme="normal"
            size="n"
            tone="default"
            view="default"
            name={props.type}
            checked={props.values.includes(props.type)}
            disabled={props.disabled}
            onChange={() =>
                props.onChange(
                    props.values.includes(props.type)
                        ? props.values.filter((x) => x !== props.type)
                        : props.values.concat(props.type),
                )
            }
        >
            {InterfaceLabel[props.type]}
        </CheckBox>
    </div>
);

interface SurfaceListProps {
    supportedSurfaces: Array<ImplicitSurface | Surface>;
    disabled?: boolean;
    values: string[];
}

const SurfaceList: React.SFC<SurfaceListProps> = ({ supportedSurfaces, disabled, values }) => (
    <section {...b('surface-section')}>
        <p {...b('caption')}>
            {t('message-available-surfaces', {
                word: values.length === 0 ? t('devices-all') : t('devices-with-screen'),
            })}
        </p>
        <ul {...b('surface-list')}>
            {surfaceIcons.map(({ Icon, surface, title }) => (
                <li
                    aria-disabled={disabled}
                    key={surface}
                    {...b('surface', {
                        disabled,
                        excluded: !supportedSurfaces.includes(surface),
                    })}
                >
                    <div {...b('icon-container')}>
                        <Icon />
                    </div>
                    {title}
                </li>
            ))}
        </ul>
    </section>
);

interface InterfaceSelectorProps {
    label: string;
    disabled?: boolean;
    values: string[];
    onChange(interfaces: string[]): void;
}

const InterfaceSelector: React.SFC<InterfaceSelectorProps> = (props) => {
    const requiredInterfaces = filterInterfaces(props.values);
    const supportedSurfaces = getSupportedSurfaces(requiredInterfaces);

    return (
        <div {...b()}>
            <Header title={t('header-surfaces')} subtitle={t('header-surfaces-subtitle')} />

            <Field label={props.label}>
                <InterfaceCheckbox type={Interface.Screen} {...props} />
                <p {...b('caption')}>{t('message-surfaces')}</p>
                <SurfaceList supportedSurfaces={supportedSurfaces} disabled={props.disabled} values={props.values} />
            </Field>
        </div>
    );
};

export default InterfaceSelector;
